import { action, makeObservable } from 'mobx'

import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import DesktopCommonStore from '~/client/src/desktop/stores/ui/DesktopCommon.store'
import PresentationModeStore from '~/client/src/desktop/stores/ui/PresentationMode.store'
import { IAppConfig } from '~/client/src/shared/Config'
import { clearProjectNotifications } from '~/client/src/shared/lists/notifications/clearProjectNotifications'
import { resetMapSelectionInputs } from '~/client/src/shared/stores/resetMapSelectionInputs'
import Guard from '~/client/src/shared/utils/Guard'

import ApiAuthService from '../../shared/services/ApiAuthService'
import ProcoreService from '../../shared/services/ProcoreService'
import StruxHubAuthService from '../../shared/services/StruxHubAuthService'
import SmartFormImportManagerStore from '../../shared/stores/domain/SmartFormImportManager.store'
import SyncRestrictionsStore from '../../shared/stores/domain/SyncRestrictions.store'
import UploadLogsStore from '../../shared/stores/domain/UploadLogs.store'
import RootStore from '../../shared/stores/Root.store'
import GraphExecutor from '../../shared/utils/GraphExecutor'
import ProjectSetUpPageStore from '../views/ProjectSetUp/ProjectSetUpPage.store'
import ProjectSetUpPage from '../views/ProjectSetUp/ProjectSetUpPages'
import AnalyticsSettingsStore from './domain/AnalyticsSettings.store'

export default class DesktopRootStore extends RootStore {
  public common: DesktopCommonStore
  public presentationModeStore: PresentationModeStore
  public syncRestrictionsStore: SyncRestrictionsStore
  public analyticsSettingsStore: AnalyticsSettingsStore
  public uploadLogsStore: UploadLogsStore
  public projectSetUpPageStore: ProjectSetUpPageStore
  public smartFormImportManagerStore: SmartFormImportManagerStore

  public constructor(
    configuration: IAppConfig,
    public state: DesktopInitialState,
    public eventsStore: DesktopEventStore,
    apiAuthService: ApiAuthService,
    struxHubAuthService: StruxHubAuthService,
    procoreService: ProcoreService,
    graphExecutor: GraphExecutor,
    isTestEnv: boolean,
  ) {
    super(
      state,
      configuration,
      eventsStore,
      DesktopCommonStore,
      apiAuthService,
      struxHubAuthService,
      procoreService,
      graphExecutor,
      isTestEnv,
    )

    Guard.requireAll({
      state,
      apiAuthService,
      eventsStore,
    })

    this.presentationModeStore = new PresentationModeStore(
      this.state,
      this.common,
    )

    this.syncRestrictionsStore = new SyncRestrictionsStore(
      this.state,
      this.locationAttributesStore,
      this.graphExecutorStore,
    )

    this.analyticsSettingsStore = new AnalyticsSettingsStore(this.eventsStore)
    this.uploadLogsStore = new UploadLogsStore(this.eventsStore)

    this.projectSetUpPageStore = new ProjectSetUpPageStore(
      this.eventsStore,
      this.common,
      this.projectMembersStore,
    )

    this.smartFormImportManagerStore = new SmartFormImportManagerStore(
      this.workflowTypesStore,
      () => {
        this.projectSetUpPageStore.navigateTo(ProjectSetUpPage.FORM_WORKFLOWS)
      },
      this.struxHubAuthService.getValidAccessToken,
    )

    makeObservable(this)
  }

  @action
  public clearOutProjectData() {
    this.smartFormImportManagerStore.cancelImport()
    this.clearOutTrackingData()
    this.messagesStore.clearList()
    clearProjectNotifications(
      this.notificationsStore,
      this.notificationsListStore,
    )
    this.photosStore.clearList()
    this.basemapsStore.clearList()
    this.sitemapsStore.clearList()
    resetMapSelectionInputs(this.globeViewsStore, this.state)
    this.sitemapItemsStore.clearList()
    this.deliveriesStore.clearList()
    this.zonesStore.clearList()
    this.gatesStore.clearList()
    this.offloadingEquipmentsStore.clearList()
    this.equipmentResourcesStore.clear()
    this.sensorResourcesStore.clear()
    this.locationResourcesStore.clear()
    this.struxTagHistoryStore.clear()
    this.buildingsStore.clearList()
    this.routesStore.clearList()
    this.deliveryVehicleTypesStore.clearList()
    this.operationRulesStore.clearList()
    this.areasStore.clearList()
    this.levelsStore.clearList()
    this.locationIntegrationsStore.clearList()
    this.logisticsObjectsStore.clearList()
    this.projectTeamsStore.clearList()
    this.projectRolesStore.clearList()
    this.weatherForecastsStore.clearData()
    this.recurringDeliveriesSettingsStore.clearData()
    this.concreteDirectOrdersStore.clearData()
    this.concreteDirectPayloadsStore.clearData()
    this.materialCategoryStore.clearList()
    this.materialResourcesStore.clear()
    this.materialsStore.clearList()
    this.analyticsSettingsStore.clearData()
    this.tilesetsStore.clearList()
    this.workflowCategoriesStore.clearList()
    this.sharedDeliveriesStore.clearList()
    this.sharedSitePermitStore.clearList()
    this.projectLocationsStore.clearList()
    this.otherLocationsStore.clearList()
  }

  public initAllFilters() {
    this.state.appliedCategoryFilters = []
  }

  public clearAllFilters() {
    const filters = [
      this.state.deliveryFilters,
      this.state.materialFilters,
      this.state.teamMembersFilters,
      this.state.sitemapFilters,
      this.state.workflowsFilters,
    ]

    filters.forEach(({ fieldsMap }) => {
      if (fieldsMap) {
        Object.values(fieldsMap).forEach(field => field.clear())
      }
    })

    this.state.deliveryRequesterCompaniesFromLastSync = []
    this.state.filters.appliedCustomFilterId = null
  }

  public clearAllCustomFilters() {
    this.customDeliveryListFiltersStore.clearData()
    this.customUploadLogFiltersStore.clearData()
    this.customWorkflowsListFiltersStore.clearData()
  }
}
